import type { LetterTemplateFormat, LetterTemplateNote } from '@myrewards/contracts';
import type { ReactElement } from 'react';
import { Icon } from '../ui';
import s from './Settings.module.css';

/** What a person is told about a template that prints its option rows on every letter (QA-044). */
export const OPTIONS_NOTE_WORDS =
  'Cash-only letters from this template will show empty option rows.';

/** The template's check found its option rows print on a cash-only letter. */
export function showsEmptyOptions(template: { notes: readonly LetterTemplateNote[] }): boolean {
  return template.notes.includes('options_on_cash_only');
}

/**
 * The note under a template that prints its option rows on every letter, with how to hide them.
 * It never stops the template being used: letters still go out, with those rows empty.
 */
export function TemplateOptionsNote({ format }: { format: LetterTemplateFormat }): ReactElement {
  return (
    <div className={s.templateNote}>
      <p className={s.templateNoteText}>
        <Icon name="info" size={16} />
        <span>{OPTIONS_NOTE_WORDS}</span>
      </p>
      <details className={s.templateNoteHow}>
        <summary>How to hide them</summary>
        {format === 'docx' ? (
          <p>
            In the Word file, start each options row’s first cell with <code>{'{#options}'}</code>{' '}
            and end its last cell with <code>{'{/options}'}</code>, for example “
            <code>{'{#options}'}</code>Options granted” and “<code>{'{options}{/options}'}</code>”.
            A letter with no options leaves those rows out. Then upload the file as a new version of
            the template.
          </p>
        ) : (
          <p>
            A PDF form can’t hide the words printed beside a field. Use a Word template with each
            options row inside <code>{'{#options}'}</code> and <code>{'{/options}'}</code>, and a
            letter with no options leaves those rows out.
          </p>
        )}
      </details>
    </div>
  );
}

/** What a person is told about a template with "your" or "the" right before its award wording. */
export const CITATION_NOTE_WORDS =
  'This template already puts a word such as “your” or “the” right before the award wording.';

/** The template's check found an article or possessive just before the award wording (APP-130). */
export function wordBeforeWording(template: { notes: readonly LetterTemplateNote[] }): boolean {
  return template.notes.includes('article_before_citation');
}

/**
 * The note under such a template, with what it means for the wording. It never stops the template
 * being used; a letter whose wording starts with the same kind of word is stopped when it's made.
 */
export function TemplateCitationNote(): ReactElement {
  return (
    <div className={s.templateNote}>
      <p className={s.templateNoteText}>
        <Icon name="info" size={16} />
        <span>{CITATION_NOTE_WORDS}</span>
      </p>
      <details className={s.templateNoteHow}>
        <summary>What this means</summary>
        <p>
          Award wording for this letter should begin with the effort itself, not “your” or “the”, or
          the letter prints both, as in “your your contribution”. If the word shouldn’t be in the
          template, take it out of the Word file and upload it as a new version.
        </p>
      </details>
    </div>
  );
}
